<script>
import Layout from '@layouts/main'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

import ContaPagarReceber from '@/src/services/conta-pagar-receber'
import {
  buildAgendaItems,
  buildDashboardMetrics,
  formatCurrency,
  formatDate,
} from './finance-helpers'

export default {
  components: { Layout, Loading },
  data() {
    return {
      isLoading: false,
      payables: [],
      receivables: [],
      metrics: {
        totalPagar: 0,
        totalReceber: 0,
        vencidasPagar: 0,
        vencidasReceber: 0,
        vencendoHoje: 0,
      },
      kpis: [
        {
          key: 'totalReceber',
          label: 'Total a receber',
          tone: 'positive',
          isCurrency: true,
        },
        {
          key: 'totalPagar',
          label: 'Total a pagar',
          tone: 'negative',
          isCurrency: true,
        },
        {
          key: 'vencendoHoje',
          label: 'Titulos para hoje',
          tone: 'warning',
        },
        {
          key: 'vencidasPagar',
          label: 'Contas vencidas',
          tone: 'neutral',
        },
      ],
      agendaFields: [
        { key: 'tipo_operacao', label: 'Tipo' },
        { key: 'descricaoConta', label: 'Descricao' },
        { key: 'cliente', label: 'Pessoa' },
        { key: 'data_vencimento', label: 'Vencimento' },
        { key: 'valorNumerico', label: 'Valor' },
        { key: 'statusLabel', label: 'Status' },
      ],
    }
  },
  computed: {
    saldoPrevisto() {
      return this.metrics.totalReceber - this.metrics.totalPagar
    },
    agendaResumo() {
      return buildAgendaItems(this.payables, this.receivables).slice(0, 8)
    },
  },
  mounted() {
    this.carregarDashboard()
  },
  methods: {
    async carregarDashboard() {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true

      try {
        const [payablesResponse, receivablesResponse] = await Promise.all([
          ContaPagarReceber.listar(token, 'PAGAR', 1, 500),
          ContaPagarReceber.listar(token, 'RECEBER', 1, 500),
        ])

        this.payables = payablesResponse.data.contas || []
        this.receivables = receivablesResponse.data.contas || []
        this.metrics = buildDashboardMetrics(this.payables, this.receivables)
      } catch (error) {
        this.$bvToast.toast(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
            'Nao foi possivel carregar o dashboard financeiro.',
          {
            variant: 'danger',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          }
        )
      } finally {
        this.isLoading = false
      }
    },
    formatCurrencyValue(value) {
      return formatCurrency(value)
    },
    formatDateValue(value) {
      return formatDate(value)
    },
    kpiClass(tone) {
      return `finance-kpi-card is-${tone}`
    },
    statusClass(item) {
      return `finance-status-badge is-${item.status}`
    },
    goTo(path) {
      this.$router.push(path)
    },
  },
}
</script>

<template>
  <Layout>
    <div class="finance-shell">
      <section class="finance-hero">
        <div>
          <span class="finance-eyebrow">Visao Geral</span>
          <h1 class="finance-title">Dashboard Financeiro</h1>
          <p class="finance-subtitle">
            Acompanhe rapidamente recebimentos, pagamentos, agenda do dia e o
            saldo previsto do caixa.
          </p>
        </div>
        <div class="finance-actions">
          <button class="btn btn-light" @click="goTo('/financeiro/agenda-financeira')">
            Abrir agenda
          </button>
          <button class="btn btn-webreceita" @click="goTo('/financeiro/fluxo-de-caixa')">
            Fluxo de caixa
          </button>
        </div>
      </section>

      <div class="finance-kpi-grid">
        <article
          v-for="kpi in kpis"
          :key="kpi.key"
          :class="kpiClass(kpi.tone)"
        >
          <span class="finance-kpi-label">{{ kpi.label }}</span>
          <strong class="finance-kpi-value">
            {{ kpi.isCurrency ? formatCurrencyValue(metrics[kpi.key]) : metrics[kpi.key] }}
          </strong>
        </article>
        <article class="finance-kpi-card is-balance">
          <span class="finance-kpi-label">Saldo previsto</span>
          <strong class="finance-kpi-value">
            {{ formatCurrencyValue(saldoPrevisto) }}
          </strong>
        </article>
      </div>

      <div class="finance-content-grid">
        <section class="finance-panel">
          <div class="finance-panel-head">
            <div>
              <h2>Resumo rapido</h2>
              <p>Atalhos para os principais fluxos do módulo financeiro.</p>
            </div>
          </div>

          <div class="finance-shortcuts">
            <button class="finance-shortcut-card" @click="goTo('/financeiro/contas-a-receber')">
              <strong>Contas a Receber</strong>
              <span>Gerencie entradas previstas e baixas.</span>
            </button>
            <button class="finance-shortcut-card" @click="goTo('/financeiro/contas-a-pagar')">
              <strong>Contas a Pagar</strong>
              <span>Controle vencimentos e pagamentos.</span>
            </button>
            <button class="finance-shortcut-card" @click="goTo('/financeiro/tipo-conta')">
              <strong>Contas Bancarias</strong>
              <span>Cadastre bancos, caixas e saldos.</span>
            </button>
            <button class="finance-shortcut-card" @click="goTo('/financeiro/centro-custo')">
              <strong>Centro de Custo</strong>
              <span>Organize a distribuicao financeira.</span>
            </button>
          </div>
        </section>

        <section class="finance-panel">
          <div class="finance-panel-head">
            <div>
              <h2>Agenda imediata</h2>
              <p>Os proximos titulos em aberto para acompanhar o caixa.</p>
            </div>
            <button class="btn btn-light" @click="goTo('/financeiro/agenda-financeira')">
              Ver tudo
            </button>
          </div>

          <b-table
            small
            responsive
            hover
            :items="agendaResumo"
            :fields="agendaFields"
            show-empty
            class="finance-table"
          >
            <template #empty>
              <div class="finance-empty-state">
                Nenhum titulo em aberto encontrado.
              </div>
            </template>
            <template #cell(tipo_operacao)="row">
              <span
                :class="[
                  'finance-type-chip',
                  row.item.tipo_operacao === 'RECEBER' ? 'is-receber' : 'is-pagar',
                ]"
              >
                {{ row.item.tipo_operacao === 'RECEBER' ? 'Receber' : 'Pagar' }}
              </span>
            </template>
            <template #cell(data_vencimento)="row">
              {{ formatDateValue(row.item.data_vencimento) }}
            </template>
            <template #cell(valorNumerico)="row">
              {{ formatCurrencyValue(row.item.valorNumerico) }}
            </template>
            <template #cell(statusLabel)="row">
              <span :class="statusClass(row.item)">
                {{ row.item.statusLabel }}
              </span>
            </template>
          </b-table>
        </section>
      </div>

      <b-overlay :show="isLoading" no-wrap />
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.finance-shell {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.finance-hero,
.finance-panel,
.finance-kpi-card,
.finance-shortcut-card {
  background: #fff;
  border: 1px solid rgba(0, 142, 117, 0.14);
  border-radius: 20px;
  box-shadow: 0 18px 34px rgba(15, 61, 52, 0.06);
}

.finance-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 28px 30px;
}

.finance-eyebrow {
  display: inline-block;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #008e75;
}

.finance-title {
  margin: 0 0 8px;
  font-size: 34px;
  color: #11493f;
}

.finance-subtitle {
  margin: 0;
  color: #64807a;
}

.finance-actions {
  display: flex;
  gap: 12px;
}

.finance-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 18px;
}

.finance-kpi-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 22px;
}

.finance-kpi-card.is-positive {
  background: linear-gradient(180deg, #f2fbf8 0%, #ffffff 100%);
}

.finance-kpi-card.is-negative {
  background: linear-gradient(180deg, #fff5f3 0%, #ffffff 100%);
}

.finance-kpi-card.is-warning {
  background: linear-gradient(180deg, #fff9ee 0%, #ffffff 100%);
}

.finance-kpi-card.is-balance {
  background: linear-gradient(180deg, #f7f8ff 0%, #ffffff 100%);
}

.finance-kpi-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #718a84;
}

.finance-kpi-value {
  font-size: 30px;
  line-height: 1.2;
  color: #11493f;
}

.finance-content-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 20px;
}

.finance-panel {
  padding: 24px;
}

.finance-panel-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.finance-panel-head h2 {
  margin: 0 0 6px;
  color: #11493f;
}

.finance-panel-head p {
  margin: 0;
  color: #69847f;
}

.finance-shortcuts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.finance-shortcut-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px;
  text-align: left;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.finance-shortcut-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 28px rgba(15, 61, 52, 0.1);
}

.finance-shortcut-card strong {
  color: #11493f;
}

.finance-shortcut-card span {
  color: #6a8580;
  font-size: 14px;
}

.finance-table::v-deep table {
  margin-bottom: 0;
}

.finance-empty-state {
  padding: 24px 12px;
  text-align: center;
  color: #6f8a84;
}

.finance-type-chip,
.finance-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.finance-type-chip.is-receber {
  background: rgba(0, 142, 117, 0.1);
  color: #0d6555;
}

.finance-type-chip.is-pagar {
  background: rgba(214, 58, 58, 0.1);
  color: #bf3030;
}

.finance-status-badge.is-overdue {
  background: rgba(214, 58, 58, 0.12);
  color: #bf3030;
}

.finance-status-badge.is-today {
  background: rgba(240, 145, 31, 0.14);
  color: #b86e12;
}

.finance-status-badge.is-upcoming,
.finance-status-badge.is-open,
.finance-status-badge.is-unknown {
  background: rgba(0, 142, 117, 0.1);
  color: #0d6555;
}

@media (max-width: 1199px) {
  .finance-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .finance-content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .finance-hero,
  .finance-panel-head,
  .finance-shortcuts {
    display: flex;
    flex-direction: column;
  }

  .finance-shortcuts {
    gap: 12px;
  }

  .finance-kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>
